import {
    Campaign,
    SplitOptimization,
    CampaignStatus,
    CampaignType,
    CampaignTriggerType,
    CampaignTemplate,
    Template,
    TimingOptionEnum,
} from '@dev/ee-api/ee-api-models';
import { Reducer } from 'redux';
import { set, orderBy, sortBy, cloneDeep, pullAt, omit } from 'lodash-es';
import { usePilotPlanConfig } from '@/services/hooks/usePilotPlanConfig';
import {
    CampaignCreatorState,
    CampaignCreatorActionTypes,
    SetDefaultCampaignAction,
    LoadCampaignStartAction,
    LoadCampaignSuccessAction,
    SetCampaignErrorsAction,
    SetCampaignErrorsSubscriberAction,
    DeselectTemplateAction,
    LoadTemplateStartAction,
    LoadTemplateSuccessAction,
    LoadTemplateErrorAction,
    CountRecipientsSuccessAction,
    SetActiveTemplateIndexAction,
    SelectedTemplateType,
    SetDefaultTargetAction,
    LoadCompletedCampaignsCountSuccessAction,
    CheckEnteredDomainSuccessAction,
    ModifyCampaignSendTestAction,
    SetAbTabsAction,
    SetSelectedTemplatesAction,
    RemoveAbTabsAction,
    AddNewCampaignTemplateAction,
    SetActiveUtmTabIndexAction,
    ModifyCampaignMapAction,
    LoadCampaignSimpleStatsSuccessAction,
    SetRecalcAbRecipientsAction,
    OpenEditorDragAndDropAction,
    OpenTemplateEditorAction,
} from './types';

interface GetDefaultCampaignProps {
    isTestingCompleted: boolean;
    defaultSenderEmail: string;
    defaultSenderName: string;
    isAbCampaign?: boolean;
    pilotPlanCampaign?: ReturnType<typeof usePilotPlanConfig>['pilotDefaultCampaignOverride'];
}

const isCampaignTypeAB = (campaign: Campaign): boolean => campaign.Type === CampaignType.AbSplit;
const isCampaignTemplateUTMSwichedOn = (campaign: Campaign): boolean =>
    campaign.CampaignTemplates?.[0].UTM && campaign.CampaignTemplates?.[0].UTM.UtmSource !== null;

const getDefaultCampaign = ({
    isTestingCompleted,
    defaultSenderEmail,
    defaultSenderName,
    isAbCampaign,
    pilotPlanCampaign,
}: GetDefaultCampaignProps): Campaign => {
    const singleTemplate = {
        Body: {
            Subject: '',
        },
        Sender: {
            From: defaultSenderEmail ? [defaultSenderEmail] : [],
            FromName: defaultSenderName || '',
            ReplyTo: defaultSenderEmail ? [defaultSenderEmail] : [],
            ReplyName: defaultSenderName || '',
        },
        UTM: null,
    };
    return {
        CampaignTemplates: isAbCampaign ? [singleTemplate, cloneDeep(singleTemplate)] : [singleTemplate],
        //[
        //   {
        //     "Name": "aaaaaa %50 bbbbbb - 1183105596",
        //     "Status": "Active",
        //     "Poolname": "default",
        //     "TemplateName": "Campaign:2022-09-20 17:44:36",
        //     "UTM": {
        //       "UtmSource": null,
        //       "UtmMedium": null,
        //       "UtmCampaign": null,
        //       "UtmContent": null
        //     },
        //     "Body": {
        //       "Subject": ""
        //     },
        //     "Sender": {
        //       "From": [
        //
        //       ],
        //       "FromName": "",
        //       "ReplyTo": [
        //
        //       ]
        //     },
        //     "Attachments": null,
        //     "Screenshot": "/ddc190cf-d400-4c77-87b1-8359aecc6ce2/templates/TYB-610FL.png",
        //     "Thumbnail": "/ddc190cf-d400-4c77-87b1-8359aecc6ce2/templates/thumbnails/TYB-610FL.jpg",
        //     "MediumScreenshot": "/ddc190cf-d400-4c77-87b1-8359aecc6ce2/templates/mediumscreenshots/TYB-610FL.jpg",
        //     "IsWinner": false,
        //     "IsWinnerTemplate": false
        //   }
        // ],
        CampaignAutomations: null,
        AutomationTrigger: null,
        TriggerOptions: {
            Type: CampaignTriggerType.SendNow,
            DateToSendOn: null,
            SendAfterXMinutes: 0,
            SendEveryXMinutes: 0,
            SendXTimes: 1,
            TriggeredBy: {
                ChannelName: null,
                Data: null,
            },
        },
        SplitOptions: {
            SplitOptimization: SplitOptimization.Opened,
            DetermineWinnerAfterXMinutes: 1440, // default 1 day in minutes
            TestingCompleted: isTestingCompleted,
        },
        RecipientsCount: 0,
        Name: '',
        Status: CampaignStatus.Draft,
        Type: isAbCampaign ? CampaignType.AbSplit : CampaignType.SendNow,
        DateAdded: new Date().toISOString(),
        FromAutomation: false,
        Targets: {
            ListNames: [],
            SegmentNames: [],
        },
        TrackingOptions: {
            TrackOpens: true,
            TrackClicks: true,
            SendStats: true,
        },
        Options: {
            TimingOption: TimingOptionEnum.None,
        },
        ...{ ...pilotPlanCampaign?.campaign },
    };
};

const defaultState: CampaignCreatorState = {
    campaign: null,
    recipientsCount: null,
    isRecipientsCountLoading: false,
    isNew: true,
    isSaved: false,
    isSaving: false,
    isLoading: false,
    isSuccess: false,
    firstErrorFocused: false,
    errorsSubscribers: [],
    templatesLoading: {},
    activeTemplateIndex: 0,
    activeUtmTabIndex: 0,
    completedCampaignsCount: null,
    completedCampaignsLoading: false,
    abTabs: [],
    selectedTemplate: [],
    abRecipients: [],
    editorDragAndDrop: {
        isOpen: false,
    },
    templateEditor: {
        isOpen: false,
    },
};

interface MapTemplateToCampaignTemplateProps {
    template: Template;
    currentCampaignTemplate: CampaignTemplate;
    defaultCampaignTemplate?: CampaignTemplate;
}

const mapTemplateToCampaignTemplate = ({
    template,
    currentCampaignTemplate,
    defaultCampaignTemplate,
}: MapTemplateToCampaignTemplateProps): CampaignTemplate => ({
    TemplateName: template.Name,
    Body: {
        Subject:
            !currentCampaignTemplate?.Body?.Subject && template.Body?.Subject
                ? template.Body.Subject
                : currentCampaignTemplate?.Body?.Subject,
        PreHeader: currentCampaignTemplate?.Body?.PreHeader,
    },
    Sender: {
        From:
            currentCampaignTemplate?.Sender.From?.[0] === defaultCampaignTemplate?.Sender?.From?.[0] &&
            template.FromEmail
                ? [template.FromEmail]
                : currentCampaignTemplate?.Sender.From,
        FromName:
            currentCampaignTemplate?.Sender.FromName === defaultCampaignTemplate?.Sender?.FromName && template.FromName
                ? template.FromName
                : currentCampaignTemplate?.Sender.FromName,
        ReplyTo:
            currentCampaignTemplate?.Sender.ReplyTo?.[0] === defaultCampaignTemplate?.Sender?.ReplyTo?.[0] &&
            template.FromEmail
                ? [template.FromEmail]
                : currentCampaignTemplate?.Sender.ReplyTo,
        ReplyName:
            currentCampaignTemplate?.Sender.ReplyName === defaultCampaignTemplate?.Sender?.ReplyName &&
            template.FromName
                ? template.FromName
                : currentCampaignTemplate?.Sender.ReplyName,
    },
    Screenshot: template.Screenshot,
    UTM: currentCampaignTemplate?.UTM ?? null,
    Poolname: currentCampaignTemplate?.Poolname ?? null,
    Attachments: currentCampaignTemplate?.Attachments ?? null,
});

const deselectCampaignTemplate = (
    campaignTemplate: CampaignTemplate,
    defaultCampaignTemplate: CampaignTemplate,
    deselectedTemplate: SelectedTemplateType
): CampaignTemplate => ({
    Body: {
        Subject: campaignTemplate?.Body?.Subject === deselectedTemplate?.Subject ? '' : campaignTemplate?.Body?.Subject,
        PreHeader: campaignTemplate?.Body?.PreHeader,
    },
    Sender: {
        From:
            campaignTemplate?.Sender.From?.[0] !==
            (defaultCampaignTemplate?.Sender?.From?.[0] && deselectedTemplate?.Sender?.From?.[0])
                ? [campaignTemplate?.Sender.From?.[0]]
                : [defaultCampaignTemplate?.Sender?.From?.[0]],
        FromName:
            campaignTemplate?.Sender.FromName !==
            (defaultCampaignTemplate?.Sender?.FromName && deselectedTemplate?.Sender?.FromName)
                ? campaignTemplate?.Sender.FromName
                : defaultCampaignTemplate?.Sender?.FromName,
        ReplyTo:
            campaignTemplate?.Sender.ReplyTo?.[0] !==
            (defaultCampaignTemplate?.Sender?.ReplyTo?.[0] && deselectedTemplate?.Sender?.ReplyTo?.[0])
                ? [campaignTemplate?.Sender.ReplyTo?.[0]]
                : [defaultCampaignTemplate?.Sender?.ReplyTo?.[0]],
        ReplyName:
            campaignTemplate?.Sender.ReplyName !==
            (defaultCampaignTemplate?.Sender?.ReplyName && deselectedTemplate?.Sender?.ReplyName)
                ? campaignTemplate?.Sender.ReplyName
                : defaultCampaignTemplate?.Sender.ReplyName,
    },
    UTM: campaignTemplate.UTM,
    Poolname: campaignTemplate.Poolname,
    Name: campaignTemplate.Name,
    TemplateName: null,
    MediumScreenshot: null,
    Screenshot: null,
    Thumbnail: null,
});

const setDefaultCampaign = (state: CampaignCreatorState, action: SetDefaultCampaignAction): CampaignCreatorState => {
    const { isTestingCompleted, defaultSenderEmail, defaultSenderName, isAbCampaign, pilotPlanCampaign } =
        action.payload;
    const defaultCampaign = getDefaultCampaign({
        isTestingCompleted,
        defaultSenderEmail,
        defaultSenderName,
        isAbCampaign,
        pilotPlanCampaign,
    });
    const defaultCampaignCopy = cloneDeep(defaultCampaign);

    const pilotPlanStateOverride = pilotPlanCampaign && omit(pilotPlanCampaign, 'campaign');

    return {
        ...state,
        campaign: defaultCampaign,
        loadedCampaign: null,
        defaultCampaign: defaultCampaignCopy,
        recipientsCount: 0,
        isRecipientsCountLoading: false,
        isLoading: false,
        isSaved: true,
        isSaving: false,
        name: null,
        isNew: true,
        templatesLoading: {},
        activeTemplateIndex: 0,
        ...pilotPlanStateOverride,
    };
};

const setDefaultTarget = (state: CampaignCreatorState, action: SetDefaultTargetAction): CampaignCreatorState => {
    const { targets } = action.payload;
    return {
        ...state,
        campaign: {
            ...state.campaign,
            Targets: targets,
        },
    };
};

const modifyCampaignMap = (state: CampaignCreatorState, action: ModifyCampaignMapAction): CampaignCreatorState => {
    const map = action.payload;
    const clonedCampaign = cloneDeep(state.campaign);
    map.forEach((value, objectPath): void => {
        set(clonedCampaign, objectPath, value);
    });
    return {
        ...state,
        campaign: clonedCampaign,
        isSaved: false,
    };
};

const modifyCampaignSendTest = (
    state: CampaignCreatorState,
    action: ModifyCampaignSendTestAction
): CampaignCreatorState => {
    const { objectPath, value } = action.payload;
    const clonedSendTestConfig = cloneDeep(state.sendTestConfig) || {};

    return {
        ...state,
        sendTestConfig: set(clonedSendTestConfig, objectPath, value),
    };
};

const loadStart = (state: CampaignCreatorState, action: LoadCampaignStartAction): CampaignCreatorState => {
    const { name } = action.payload;
    return {
        ...state,
        name,
        isLoading: true,
        campaign: null,
        loadedCampaign: null,
        defaultCampaign: null,
        activeTemplateIndex: 0,
    };
};

const loadSuccess = (state: CampaignCreatorState, action: LoadCampaignSuccessAction): CampaignCreatorState => {
    const campaign = action.payload;
    const winnerTemplateIndex = action.payload.CampaignTemplates?.findIndex((template): boolean => !!template.IsWinner);
    const isWinnerTemplate = winnerTemplateIndex > -1;
    if (isWinnerTemplate) {
        pullAt(campaign.CampaignTemplates, [winnerTemplateIndex]);
    }
    return {
        ...state,
        campaign,
        recipientsCount: 0,
        isRecipientsCountLoading: true,
        loadedCampaign: campaign,
        name: campaign.Name,
        isLoading: false,
        isSaved: true,
        isNew: false,
        templatesLoading: {},
    };
};

const loadError = (state: CampaignCreatorState): CampaignCreatorState => ({
    ...state,
    name: null,
    campaign: null,
    isLoading: false,
    isSaved: false,
});

const saveStart = (state: CampaignCreatorState): CampaignCreatorState => ({
    ...state,
    isSaved: false,
    isSaving: true,
    isSuccess: false,
    campaign: {
        ...state.campaign,
        Name: state.campaign?.Name || new Date().toISOString(),
    },
});

const saveSuccess = (state: CampaignCreatorState): CampaignCreatorState => ({
    ...state,
    isSaved: true,
    isSaving: false,
    isNew: false,
    isSuccess: true,
});

const saveError = (state: CampaignCreatorState): CampaignCreatorState => ({
    ...state,
    isSaving: false,
    isSuccess: false,
});

const selectTemplate = (state: CampaignCreatorState): CampaignCreatorState => ({
    ...state,
    isGalleryOpen: false,
    isSaved: false,
});

const deselectTemplate = (state: CampaignCreatorState, action: DeselectTemplateAction): CampaignCreatorState => {
    const { index } = action.payload;
    return {
        ...state,
        campaign: set(
            cloneDeep(state.campaign),
            `CampaignTemplates[${index}]`,
            deselectCampaignTemplate(
                state.campaign?.CampaignTemplates?.[index],
                state.defaultCampaign?.CampaignTemplates[index],
                state.selectedTemplate?.[index]
            )
        ),
        selectedTemplate: set(cloneDeep(state.selectedTemplate), `[${index}]`, {}),
    };
};

const removeCampaignTemplate = (state: CampaignCreatorState, action: RemoveAbTabsAction): CampaignCreatorState => {
    const { id } = action.payload;
    const newCampaignTemplates = cloneDeep(state.campaign.CampaignTemplates);
    newCampaignTemplates.splice(id, 1);

    const newSelectedTemplates = cloneDeep(state.selectedTemplate);
    newSelectedTemplates.splice(id, 1);

    return {
        ...state,
        campaign: {
            ...state.campaign,
            CampaignTemplates: newCampaignTemplates,
        },
        selectedTemplate: newSelectedTemplates,
    };
};

const addNewCampaignTemplate = (
    state: CampaignCreatorState,
    action: AddNewCampaignTemplateAction
): CampaignCreatorState => {
    const { defaultSenderEmail, defaultSenderName } = action.payload;
    const newCampaignTemplates = cloneDeep(state.campaign.CampaignTemplates);

    const defaultCampaignTemplate = {
        Body: {
            Subject: '',
            PreHeader: '',
        },
        Sender: {
            From: defaultSenderEmail ? [defaultSenderEmail] : [],
            FromName: defaultSenderName || '',
            ReplyTo: defaultSenderEmail ? [defaultSenderEmail] : [],
            ReplyName: defaultSenderName || '',
        },
        UTM: null,
    };
    setTemplateUTM(
        defaultCampaignTemplate,
        newCampaignTemplates.length,
        isCampaignTypeAB(state.campaign),
        isCampaignTemplateUTMSwichedOn(state.campaign)
    );

    newCampaignTemplates.push(defaultCampaignTemplate);

    return {
        ...state,
        campaign: {
            ...state.campaign,
            CampaignTemplates: newCampaignTemplates,
        },
        selectedTemplate: [...state.selectedTemplate, {}],
    };
};

const openGallery = (state: CampaignCreatorState): CampaignCreatorState => ({
    ...state,
    isGalleryOpen: true,
});

const closeGallery = (state: CampaignCreatorState): CampaignCreatorState => ({
    ...state,
    isGalleryOpen: false,
});

const openEditorDragAndDrop = (
    state: CampaignCreatorState,
    action: OpenEditorDragAndDropAction
): CampaignCreatorState => ({
    ...state,
    editorDragAndDrop: {
        isOpen: true,
        sourceTemplateName: action.payload.templateName,
        isPublic: action.payload.isPublic,
        isEditedFromGallery: action.payload.isEditedFromGallery || false,
    },
});

const closeEditorDragAndDrop = (state: CampaignCreatorState): CampaignCreatorState => ({
    ...state,
    editorDragAndDrop: {
        isOpen: false,
        sourceTemplateName: null,
        isPublic: null,
    },
});

const openTemplateEditor = (state: CampaignCreatorState, action: OpenTemplateEditorAction): CampaignCreatorState => ({
    ...state,
    templateEditor: {
        isOpen: true,
        sourceTemplateName: action.payload.templateName,
    },
});

const closeTemplateEditor = (state: CampaignCreatorState): CampaignCreatorState => ({
    ...state,
    templateEditor: {
        isOpen: false,
    },
});

const setErrors = (state: CampaignCreatorState, action: SetCampaignErrorsAction): CampaignCreatorState => {
    const { errors, activeTabsIndexes } = action.payload;
    return {
        ...state,
        errors,
        activeTemplateIndex:
            activeTabsIndexes?.activeTemplateIndex > -1
                ? activeTabsIndexes?.activeTemplateIndex
                : state.activeTemplateIndex,
        activeUtmTabIndex:
            activeTabsIndexes?.activeUtmTabIndex > -1 ? activeTabsIndexes?.activeUtmTabIndex : state.activeUtmTabIndex,
        firstErrorFocused: false,
    };
};

const setErrorsSubscribers = (
    state: CampaignCreatorState,
    action: SetCampaignErrorsSubscriberAction
): CampaignCreatorState => {
    if (
        state.errorsSubscribers.findIndex((subscriber): boolean => subscriber.errorName === action.payload.errorName) >
        -1
    ) {
        return state;
    }
    return {
        ...state,
        errorsSubscribers: orderBy([...state.errorsSubscribers, action.payload], 'order'),
    };
};

const setFirstErrorFocused = (state: CampaignCreatorState): CampaignCreatorState => ({
    ...state,
    firstErrorFocused: true,
});

const loadTemplateStart = (state: CampaignCreatorState, action: LoadTemplateStartAction): CampaignCreatorState => ({
    ...state,
    templatesLoading: { ...state.templatesLoading, [action.payload.index]: action.payload.templateName },
});

const loadTemplateSuccess = (state: CampaignCreatorState, action: LoadTemplateSuccessAction): CampaignCreatorState => {
    const { template, index, isFromGallery } = action.payload;
    const newCampaignTemplate = mapTemplateToCampaignTemplate({
        template,
        currentCampaignTemplate: state.campaign?.CampaignTemplates[index],
        defaultCampaignTemplate: state.defaultCampaign?.CampaignTemplates[index],
    });

    return {
        ...state,
        campaign: set(cloneDeep(state.campaign), `CampaignTemplates[${index}]`, newCampaignTemplate),
        templatesLoading: { ...state.templatesLoading, [index]: null },
        selectedTemplate: set(cloneDeep(state.selectedTemplate), `[${index}]`, {
            Sender: {
                FromName: template.FromName,
                From: [template.FromEmail],
                ReplyName: template.FromName,
                ReplyTo: [template.FromEmail],
            },
            Subject: template.Body.Subject,
            templateType: template.TemplateType,
            isFromGallery,
        }),
    };
};

const loadTemplateError = (state: CampaignCreatorState, action: LoadTemplateErrorAction): CampaignCreatorState => ({
    ...state,
    templatesLoading: { ...state.templatesLoading, [action.payload.index]: null },
});

const countRecipientsStart = (state: CampaignCreatorState): CampaignCreatorState => ({
    ...state,
    isRecipientsCountLoading: true,
});

const countRecipientsSuccess = (
    state: CampaignCreatorState,
    action: CountRecipientsSuccessAction
): CampaignCreatorState => ({
    ...state,
    campaign: { ...state.campaign, RecipientsCount: action.payload },
    recipientsCount: action.payload,
    isRecipientsCountLoading: false,
});

const countRecipientsError = (state: CampaignCreatorState): CampaignCreatorState => ({
    ...state,
    recipientsCount: null,
    isRecipientsCountLoading: false,
});

const setActiveTemplateIndex = (
    state: CampaignCreatorState,
    action: SetActiveTemplateIndexAction
): CampaignCreatorState => ({
    ...state,
    activeTemplateIndex: action.payload,
});

const setActiveUtmTabIndex = (
    state: CampaignCreatorState,
    action: SetActiveUtmTabIndexAction
): CampaignCreatorState => ({
    ...state,
    activeUtmTabIndex: action.payload,
});

const setAbTabs = (state: CampaignCreatorState, action: SetAbTabsAction): CampaignCreatorState => ({
    ...state,
    abTabs: action.payload.abTabs,
    activeUtmTabIndex:
        state.activeUtmTabIndex === action.payload.abTabs.length
            ? action.payload.abTabs.length - 1
            : state.activeUtmTabIndex,
});

const setAbRecipients = (state: CampaignCreatorState, action: SetRecalcAbRecipientsAction): CampaignCreatorState => ({
    ...state,
    abRecipients: action.payload.abRecipients,
});

const setAbTabsError = (state: CampaignCreatorState): CampaignCreatorState => ({
    ...state,
    abTabs: [],
});

const loadCompletedCampaignsCountStart = (state: CampaignCreatorState): CampaignCreatorState => ({
    ...state,
    completedCampaignsLoading: true,
});

const loadCompletedCampaignsCountSuccess = (
    state: CampaignCreatorState,
    action: LoadCompletedCampaignsCountSuccessAction
): CampaignCreatorState => ({
    ...state,
    completedCampaignsCount: action.payload,
    completedCampaignsLoading: false,
});

const loadCompletedCampaignsCountError = (state: CampaignCreatorState): CampaignCreatorState => ({
    ...state,
    completedCampaignsCount: null,
    completedCampaignsLoading: false,
});

const checkEnteredDomainSuccess = (
    state: CampaignCreatorState,
    action: CheckEnteredDomainSuccessAction
): CampaignCreatorState => {
    const { isFreeDomain } = action.payload;
    return {
        ...state,
        isFromEmailFree: isFreeDomain,
    };
};

const checkEnteredDomainError = (state: CampaignCreatorState): CampaignCreatorState => ({
    ...state,
    isFromEmailFree: false,
});

const openPreviewOverlay = (state: CampaignCreatorState): CampaignCreatorState => ({
    ...state,
    isPreviewOverlayOpen: true,
});

const closePreviewOverlay = (state: CampaignCreatorState): CampaignCreatorState => ({
    ...state,
    isPreviewOverlayOpen: false,
});

const setTemplateUTM = (
    template: CampaignTemplate,
    index: number,
    isTypeAB: boolean,
    swichOn: boolean
): CampaignTemplate => {
    if (swichOn) {
        template.UTM = {
            UtmSource: 'Newsletter',
            UtmMedium: 'email',
            UtmCampaign: 'Campaign+Name',
        };
        if (isTypeAB) {
            template.UTM.UtmContent = `Version+${String.fromCharCode(65 + index)}`;
        }
        return;
    }
    template.UTM = {
        UtmCampaign: null,
        UtmMedium: null,
        UtmSource: null,
    };
    if (isTypeAB) {
        template.UTM.UtmContent = null;
    }
    return template;
};

const switchUTM = (state: CampaignCreatorState): CampaignCreatorState => {
    const newCampaign = cloneDeep(state.campaign);
    const isTypeAB = isCampaignTypeAB(newCampaign);
    const isUTM = isCampaignTemplateUTMSwichedOn(newCampaign);
    newCampaign.CampaignTemplates?.forEach((template, index): void => {
        setTemplateUTM(template, index, isTypeAB, !isUTM);
    });
    return {
        ...state,
        campaign: newCampaign,
    };
};

const setSelectedTemplates = (
    state: CampaignCreatorState,
    action: SetSelectedTemplatesAction
): CampaignCreatorState => {
    const { selectedTemplates } = action.payload;
    return {
        ...state,
        selectedTemplate: selectedTemplates,
    };
};

const loadSimpleStatsStart = (state: CampaignCreatorState): CampaignCreatorState => ({
    ...state,
    campaignSimpleStatsLoading: true,
});

const loadSimpleStatsSuccess = (
    state: CampaignCreatorState,
    action: LoadCampaignSimpleStatsSuccessAction
): CampaignCreatorState => ({
    ...state,
    campaignSimpleStats: {
        ...action.payload.campaignSimpleStats,
        CampaignTemplatesSummary: sortBy(action.payload.campaignSimpleStats?.CampaignTemplatesSummary, (a): number =>
            a.IsWinner ? 1 : 0
        ),
    },
    campaignSimpleStatsLoading: false,
});

const loadSimpleStatsError = (state: CampaignCreatorState): CampaignCreatorState => ({
    ...state,
    campaignSimpleStats: null,
    campaignSimpleStatsLoading: false,
});

const loadAttachmentStart = (state: CampaignCreatorState): CampaignCreatorState => ({
    ...state,
    attachmentLoading: true,
});

const loadAttachmentFinish = (state: CampaignCreatorState): CampaignCreatorState => ({
    ...state,
    attachmentLoading: false,
});

const reducer: Reducer<CampaignCreatorState> = (
    state: CampaignCreatorState = defaultState,
    action
): CampaignCreatorState => {
    // eslint-disable-next-line sonarjs/max-switch-cases
    switch (action.type) {
        case CampaignCreatorActionTypes.SET_DEFAULT_CAMPAIGN:
            return setDefaultCampaign(state, action as SetDefaultCampaignAction);
        case CampaignCreatorActionTypes.MODIFY_CAMPAIGN_MAP:
            return modifyCampaignMap(state, action as ModifyCampaignMapAction);
        case CampaignCreatorActionTypes.MODIFY_CAMPAIGN_SEND_TEST:
            return modifyCampaignSendTest(state, action as ModifyCampaignSendTestAction);
        case CampaignCreatorActionTypes.LOAD_START:
            return loadStart(state, action as LoadCampaignStartAction);
        case CampaignCreatorActionTypes.LOAD_SUCCESS:
            return loadSuccess(state, action as LoadCampaignSuccessAction);
        case CampaignCreatorActionTypes.LOAD_ERROR:
            return loadError(state);
        case CampaignCreatorActionTypes.SAVE_START:
            return saveStart(state);
        case CampaignCreatorActionTypes.SAVE_SUCCESS:
            return saveSuccess(state);
        case CampaignCreatorActionTypes.SAVE_ERROR:
            return saveError(state);
        case CampaignCreatorActionTypes.SET_CAMPAIGN_ERRORS:
            return setErrors(state, action as SetCampaignErrorsAction);
        case CampaignCreatorActionTypes.SET_CAMPAIGN_ERRORS_SUBSCRIBER:
            return setErrorsSubscribers(state, action as SetCampaignErrorsSubscriberAction);
        case CampaignCreatorActionTypes.SET_FRIST_ERROR_FOCUSED:
            return setFirstErrorFocused(state);
        case CampaignCreatorActionTypes.SELECT_TEMPLATE:
            return selectTemplate(state);
        case CampaignCreatorActionTypes.DESELECT_TEMPLATE:
            return deselectTemplate(state, action as DeselectTemplateAction);
        case CampaignCreatorActionTypes.OPEN_GALLERY:
            return openGallery(state);
        case CampaignCreatorActionTypes.CLOSE_GALLERY:
            return closeGallery(state);
        case CampaignCreatorActionTypes.OPEN_EDITOR_DRAG_AND_DROP:
            return openEditorDragAndDrop(state, action as OpenEditorDragAndDropAction);
        case CampaignCreatorActionTypes.CLOSE_EDITOR_DRAG_AND_DROP:
            return closeEditorDragAndDrop(state);
        case CampaignCreatorActionTypes.OPEN_TEMPLATE_EDITOR:
            return openTemplateEditor(state, action as OpenTemplateEditorAction);
        case CampaignCreatorActionTypes.CLOSE_TEMPLATE_EDITOR:
            return closeTemplateEditor(state);
        case CampaignCreatorActionTypes.CLEAR_PICKED_CAMAPAIGN:
            return defaultState;
        case CampaignCreatorActionTypes.LOAD_TEMPLATE_START:
            return loadTemplateStart(state, action as LoadTemplateStartAction);
        case CampaignCreatorActionTypes.LOAD_TEMPLATE_SUCCESS:
            return loadTemplateSuccess(state, action as LoadTemplateSuccessAction);
        case CampaignCreatorActionTypes.LOAD_TEMPLATE_ERROR:
            return loadTemplateError(state, action as LoadTemplateErrorAction);
        case CampaignCreatorActionTypes.COUNT_RECIPIENTS_START:
            return countRecipientsStart(state);
        case CampaignCreatorActionTypes.COUNT_RECIPIENTS_SUCCESS:
            return countRecipientsSuccess(state, action as CountRecipientsSuccessAction);
        case CampaignCreatorActionTypes.COUNT_RECIPIENTS_ERROR:
            return countRecipientsError(state);
        case CampaignCreatorActionTypes.SET_ACTIVE_TEMPLATE_INDEX:
            return setActiveTemplateIndex(state, action as SetActiveTemplateIndexAction);
        case CampaignCreatorActionTypes.SET_ACTIVE_UTM_TAB_INDEX:
            return setActiveUtmTabIndex(state, action as SetActiveUtmTabIndexAction);
        case CampaignCreatorActionTypes.SET_DEFAULT_TARGET:
            return setDefaultTarget(state, action as SetDefaultTargetAction);
        case CampaignCreatorActionTypes.LOAD_COMPLETED_CAMPAIGNS_COUNT_START:
            return loadCompletedCampaignsCountStart(state);
        case CampaignCreatorActionTypes.LOAD_COMPLETED_CAMPAIGNS_COUNT_SUCCESS:
            return loadCompletedCampaignsCountSuccess(state, action as LoadCompletedCampaignsCountSuccessAction);
        case CampaignCreatorActionTypes.LOAD_COMPLETED_CAMPAIGNS_COUNT_ERROR:
            return loadCompletedCampaignsCountError(state);
        case CampaignCreatorActionTypes.CHECK_ENTERED_DOMAIN_SUCCESS:
            return checkEnteredDomainSuccess(state, action as CheckEnteredDomainSuccessAction);
        case CampaignCreatorActionTypes.CHECK_ENTERED_DOMAIN_ERROR:
            return checkEnteredDomainError(state);
        case CampaignCreatorActionTypes.SET_AB_TABS:
            return setAbTabs(state, action as SetAbTabsAction);
        case CampaignCreatorActionTypes.SET_AB_TABS_ERROR:
            return setAbTabsError(state);
        case CampaignCreatorActionTypes.SET_RECALC_AB_RECIPIENTS:
            return setAbRecipients(state, action as SetRecalcAbRecipientsAction);
        case CampaignCreatorActionTypes.OPEN_PREVIEW_OVERLAY:
            return openPreviewOverlay(state);
        case CampaignCreatorActionTypes.CLOSE_PREVIEW_OVERLAY:
            return closePreviewOverlay(state);
        case CampaignCreatorActionTypes.SWITCH_UTM:
            return switchUTM(state);
        case CampaignCreatorActionTypes.SET_SELECTED_TEMPLATES:
            return setSelectedTemplates(state, action as SetSelectedTemplatesAction);
        case CampaignCreatorActionTypes.REMOVE_AB_TABS:
            return removeCampaignTemplate(state, action as RemoveAbTabsAction);
        case CampaignCreatorActionTypes.ADD_NEW_CAMPAIGN_TEMPLATE:
            return addNewCampaignTemplate(state, action as AddNewCampaignTemplateAction);
        case CampaignCreatorActionTypes.LOAD_CAMPAIGN_SIMPLE_STATS_START:
            return loadSimpleStatsStart(state);
        case CampaignCreatorActionTypes.LOAD_CAMPAIGN_SIMPLE_STATS_SUCCESS:
            return loadSimpleStatsSuccess(state, action as LoadCampaignSimpleStatsSuccessAction);
        case CampaignCreatorActionTypes.LOAD_CAMPAIGN_SIMPLE_STATS_ERROR:
            return loadSimpleStatsError(state);
        case CampaignCreatorActionTypes.LOAD_ATTACHMENT_START:
            return loadAttachmentStart(state);
        case CampaignCreatorActionTypes.LOAD_ATTACHMENT_FINISH:
            return loadAttachmentFinish(state);
        default:
            return state;
    }
};

export { reducer as campaignCreator };

export default reducer;
